import React, { useState, useEffect, useMemo } from 'react';
import {
    Modal, Form, Input, Select, Button, Table, Row, Col, Space,
    Typography, DatePicker, message, InputNumber, Popover, Tag, Tooltip
} from 'antd';
import {
    ShoppingCartOutlined, TagOutlined, DollarOutlined, WalletOutlined,
    BankOutlined, ShopOutlined, DeleteOutlined, InfoCircleOutlined, SearchOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useSaveCompra } from '../../../hooks/useCompras';
import { useGetBodegas } from '../../../hooks/useBodegas';
import { useConfig } from '../../../context/ConfigContext';
import { useGetStockBodega } from '../../../hooks/useBodegas';

const { Text, Title } = Typography;
const { Option } = Select;

const formatMoney = (val) => {
    if (!val) return '';
    const parts = val.toString().split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return parts.join(',');
};
const parseMoney = (val) => val.replace(/\./g, '').replace(/,/g, '.');

const ModalNuevaCompra = ({ open, onClose, cajaActiva, proveedoresDB, productosDB, compraAEditar }) => {
    const [form] = Form.useForm();
    const saveCompra = useSaveCompra();

    const proveedorSeleccionadoId = Form.useWatch('proveedor', form);
    const proveedorInfo = proveedoresDB.find(p => p.id === proveedorSeleccionadoId);

    const [items, setItems] = useState([]);

    const { config } = useConfig();
    const isPremium = config?.plan_actual === 'premium';
    const { data: bodegas = [] } = useGetBodegas();

    const bodegaDestinoForm = Form.useWatch('bodega_destino', form);
    const bodegaDestinoIdFinal = (isPremium && bodegaDestinoForm) 
        ? bodegaDestinoForm 
        : bodegas.find(b => b.es_default)?.id;

    const { data: stockBodegaDestino = [] } = useGetStockBodega(bodegaDestinoIdFinal);

    // Solo permitimos bodegas físicas (Principal o Secundarias). Nada de vehículos.
    const bodegasFisicas = useMemo(() => bodegas.filter(b => b.activa && b.tipo !== 'vendedor'), [bodegas]);

    // Cargar datos si es edición o limpiar si es nueva
    useEffect(() => {
        if (open) {
            if (compraAEditar) {
                form.setFieldsValue({
                    proveedor: compraAEditar.proveedor_id,
                    factura: compraAEditar.numero_factura,
                    fecha: dayjs(compraAEditar.fecha),
                    metodo_pago: compraAEditar.metodo_pago
                });
                const itemsEdit = (compraAEditar.items || []).map(i => {
                    const prod = productosDB.find(p => p.id === i.producto_id) || {};
                    return {
                        key: i.producto_id,
                        producto_id: i.producto_id,
                        nombre: prod.nombre || 'Producto',
                        cantidad: i.cantidad,
                        costo_base: i.costo_unitario,
                        iva: 0,
                        costo_final: i.costo_unitario,
                        subtotal: i.subtotal,
                        precio_venta_actual: prod.precio_venta || 0,
                        nuevo_precio_venta: null
                    };
                });
                setItems(itemsEdit);
            } else {
                form.resetFields();
                form.setFieldsValue({ fecha: dayjs() });
                setItems([]);
            }
        }
    }, [open, compraAEditar, form, productosDB]);

    const totalCompra = items.reduce((sum, item) => sum + Number(item.subtotal || 0), 0);

    const opcionesProductos = useMemo(() => {
        const opciones = [];
        productosDB.forEach(p => {
            if (p.has_variants && p.variantes?.length > 0) {
                p.variantes.forEach(v => {
                    const nombreVariante = `${p.nombre} - ${v.atributos?.nombre || 'Variante'}`;
                    opciones.push({
                        value: v.id, // Usamos el ID de la variante como identificador único
                        is_variant: true,
                        padre_id: p.id,
                        data_variante: v,
                        data_padre: p,
                        search_str: `${nombreVariante} ${v.codigo_interno || ''} ${v.codigo_barras || ''}`,
                        label: (
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text strong>{nombreVariante}</Text>
                                <Text type="secondary">{v.codigo_barras || v.codigo_interno}</Text>
                            </div>
                        )
                    });
                });
            } else {
                opciones.push({
                    value: p.id,
                    is_variant: false,
                    data_padre: p,
                    search_str: `${p.nombre} ${p.sku || p.codigo_interno || ''} ${p.codigo_barras || ''}`,
                    label: (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Text strong>{p.nombre}</Text>
                            <Text type="secondary">{p.sku || p.codigo_interno || p.codigo_barras}</Text>
                        </div>
                    )
                });
            }
        });
        return opciones;
    }, [productosDB]);

    // ==========================================
    // BUSCADOR UNIVERSAL (CÓDIGO Y TEXTO)
    // ==========================================
    const handleSeleccionarProducto = (val, option) => {
        if (!option) return;

        const { is_variant, data_padre, data_variante, value: uniqueId } = option;

        setItems(prevItems => {
            const index = prevItems.findIndex(i => i.unique_id === uniqueId);
            if (index >= 0) {
                const nuevos = [...prevItems];
                nuevos[index].cantidad += 1;
                nuevos[index].subtotal = parseFloat((nuevos[index].cantidad * nuevos[index].costo_final).toFixed(2));
                return nuevos;
            } else {
                const costoFinal = is_variant ? (data_variante.precio_compra || data_padre.precio_compra || 0) : (data_padre.precio_compra || 0);
                const precioVenta = is_variant ? (data_variante.precio_base || data_padre.precio_venta || 0) : (data_padre.precio_venta || 0);
                const nombreItem = is_variant ? `${data_padre.nombre} - ${data_variante.atributos?.nombre || 'Variante'}` : data_padre.nombre;
                const stockActual = is_variant ? (data_variante.stock || 0) : (data_padre.stock || 0);

                return [...prevItems, {
                    key: uniqueId,
                    unique_id: uniqueId, // Clave única para la tabla
                    producto_id: data_padre.id,
                    variante_id: is_variant ? data_variante.id : null,
                    nombre: nombreItem,
                    cantidad: 1,
                    costo_base: costoFinal,
                    iva: 0,
                    costo_final: costoFinal,
                    subtotal: costoFinal,
                    precio_venta_actual: precioVenta,
                    nuevo_precio_venta: null,
                    stock_actual: stockActual
                }];
            }
        });
    };

    const eliminarItem = (id) => setItems(items.filter(i => i.unique_id !== id));

    const actualizarFila = (id, campo, valor) => {
        setItems(prev => prev.map(item => {
            if (item.unique_id !== id) return item;
            const actualizado = { ...item, [campo]: valor };

            if (campo === 'costo_base' || campo === 'iva') {
                const base = parseFloat(actualizado.costo_base || 0);
                const impuesto = (base * parseFloat(actualizado.iva || 0)) / 100;
                actualizado.costo_final = parseFloat((base + impuesto).toFixed(2));
            }

            actualizado.subtotal = Number((Number(actualizado.cantidad) * Number(actualizado.costo_final)).toFixed(2));
            return actualizado;
        }));
    };

    const procesarCompra = async (values) => {
        if (items.length === 0) return message.error("Agrega productos a la compra.");

        const itemConPerdida = items.find(i => {
            const precioVentaAValidar = Number(i.nuevo_precio_venta !== null ? i.nuevo_precio_venta : i.precio_venta_actual);
            return precioVentaAValidar < Number(i.costo_final);
        });

        if (itemConPerdida) {
            return message.error(`Error: El producto "${itemConPerdida.nombre}" tiene un precio de venta ($${formatMoney(itemConPerdida.nuevo_precio_venta || itemConPerdida.precio_venta_actual)}) menor a su costo final ($${formatMoney(itemConPerdida.costo_final)}). Ajusta el precio para continuar.`);
        }

        if (values.metodo_pago === 'Efectivo Caja') {
            if (!cajaActiva) return message.error("No hay una sesión de caja abierta.");
            const fechaCompra = values.fecha.startOf('day');
            const fechaCaja = dayjs(cajaActiva.fecha_apertura).startOf('day');
            if (fechaCompra.isBefore(fechaCaja)) {
                return message.error("No puedes pagar con la caja actual una factura con fecha anterior. Usa 'Efectivo Externo'.");
            }
        }

        const payload = {
            id: compraAEditar?.id,
            proveedor_id: values.proveedor,
            bodega_id: isPremium ? values.bodega_destino : null,
            numero_factura: values.factura || null,
            metodo_pago: values.metodo_pago,
            fecha: values.fecha.toISOString(),
            estado_pago: values.metodo_pago === 'Crédito' ? 'Por Pagar' : 'Pagado',
            sesion_caja_id: values.metodo_pago === 'Efectivo Caja' ? cajaActiva?.id : null,
            total: totalCompra,
            items: items.map(i => ({
                producto_id: i.producto_id,
                variante_id: i.variante_id || null,
                cantidad: i.cantidad,
                costo_unitario: i.costo_final,
                subtotal: i.subtotal,
                nuevo_precio_venta: i.nuevo_precio_venta
            }))
        };

        saveCompra.mutate(payload, {
            onSuccess: () => {
                message.success(compraAEditar ? "Compra actualizada" : "Compra registrada exitosamente.");
                onClose();
            }
        });
    };

    // ==========================================
    // COLUMNAS DE LA TABLA
    // ==========================================
    const columnas = [
        {
            title: 'Producto',
            dataIndex: 'nombre',
            width: 240,
            render: (txt, record) => {
                const registroStock = stockBodegaDestino.find(sb => 
                    sb.producto_id === record.producto_id && 
                    (record.variante_id ? sb.variante_id === record.variante_id : !sb.variante_id)
                );
                const stockReal = registroStock ? Number(registroStock.stock) : 0;

                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <Text strong style={{ fontSize: '13px', color: '#0f172a' }}>{txt}</Text>
                        <Text type="secondary" style={{ fontSize: '11px' }}>
                            Stock actual: <Tag style={{ margin: 0, fontSize: '10px' }} color={stockReal <= 5 ? 'error' : 'default'}>{stockReal} unds</Tag>
                        </Text>
                    </div>
                );
            }
        },
        {
            title: 'Cant.',
            dataIndex: 'cantidad',
            width: 90,
            render: (val, record) => (
                <InputNumber
                    min={1} value={val} size="middle" style={{ width: '100%' }} controls={false} // Sin flechas
                    onChange={v => actualizarFila(record.unique_id, 'cantidad', v || 1)}
                    onFocus={(e) => e.target.select()}
                />
            )
        },
        {
            title: 'Costo Base ($)',
            dataIndex: 'costo_base',
            width: 130,
            render: (val, record) => (
                <InputNumber
                    min={0} value={val} size="middle" style={{ width: '100%' }} controls={false} // Sin flechas
                    formatter={formatMoney} parser={parseMoney}
                    onChange={v => actualizarFila(record.unique_id, 'costo_base', v || 0)}
                    onFocus={(e) => e.target.select()}
                />
            )
        },
        {
            title: (
                <Space size={4}>
                    IVA %
                    <Tooltip title="Solo selecciona si la factura física te cobra el IVA por separado." color="#0f172a">
                        <InfoCircleOutlined style={{ color: '#64748b' }} />
                    </Tooltip>
                </Space>
            ),
            dataIndex: 'iva',
            width: 90,
            render: (val, record) => (
                <Select value={val} size="middle" style={{ width: '100%' }} onChange={v => actualizarFila(record.unique_id, 'iva', v)}>
                    <Option value={0}>0%</Option>
                    <Option value={5}>5%</Option>
                    <Option value={19}>19%</Option>
                </Select>
            )
        },
        {
            title: 'Costo Final',
            dataIndex: 'costo_final',
            width: 110,
            align: 'right',
            render: (val) => <Text type="secondary">${formatMoney(val)}</Text>
        },
        {
            title: 'Subtotal',
            dataIndex: 'subtotal',
            width: 120,
            align: 'right',
            render: (val) => <Text strong style={{ color: '#10b981', fontSize: '14px' }}>${formatMoney(val)}</Text>
        },
        {
            title: 'Precio Venta',
            width: 140,
            align: 'right', // Lo alineamos a la derecha para que los números cuadren visualmente
            render: (_, record) => {
                // Tomamos el precio nuevo si lo editó, o el actual si no lo ha tocado
                const pVentaReal = record.nuevo_precio_venta || record.precio_venta_actual;
                // Calculamos el margen bruto
                const margen = pVentaReal > 0 ? (((pVentaReal - record.costo_final) / pVentaReal) * 100).toFixed(1) : 0;

                // Le damos color al texto del margen dependiendo de la ganancia
                const colorMargen = margen < 20 ? '#ff4d4f' : margen < 40 ? '#faad14' : '#52c41a';

                return (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
                        {/* Mostramos el precio y el margen a simple vista */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: '1.2' }}>
                            <Text strong style={{ fontSize: '13px' }}>${formatMoney(pVentaReal)}</Text>
                            <Text style={{ fontSize: '11px', color: colorMargen, fontWeight: '500' }}>
                                Margen: {margen}%
                            </Text>
                        </div>

                        {/* El Popover solo se abre si presiona el botón */}
                        <Popover
                            title={<Text strong>Ajustar Precio de Venta</Text>}
                            trigger="click"
                            content={
                                <div style={{ width: '220px', padding: '5px' }}>
                                    <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginBottom: '8px' }}>
                                        Costo Final pagado: <b>${formatMoney(record.costo_final)}</b>
                                    </Text>
                                    <InputNumber
                                        size="large" prefix="$" style={{ width: '100%', marginBottom: '10px' }} controls={false}
                                        formatter={formatMoney} parser={parseMoney}
                                        value={record.nuevo_precio_venta || record.precio_venta_actual}
                                        min={record.costo_final}
                                        onChange={v => actualizarFila(record.unique_id, 'nuevo_precio_venta', v)}
                                        onFocus={(e) => e.target.select()}
                                    />
                                    <Tag color={margen < 20 ? 'error' : margen < 40 ? 'warning' : 'success'} style={{ width: '100%', textAlign: 'center', margin: 0, padding: '4px' }}>
                                        Rentabilidad: {margen}%
                                    </Tag>
                                </div>
                            }
                        >
                            <Tooltip title="Modificar Precio">
                                <Button
                                    size="small"
                                    icon={<TagOutlined />}
                                    style={{
                                        borderColor: record.nuevo_precio_venta ? '#10b981' : '#cbd5e1',
                                        color: record.nuevo_precio_venta ? '#10b981' : '#64748b'
                                    }}
                                />
                            </Tooltip>
                        </Popover>
                    </div>
                );
            }
        },
        {
            title: '',
            width: 60,
            align: 'center',
            // Botón eliminar visible corporativo (Rojo sólido suave)
            render: (_, r) => <Button danger icon={<DeleteOutlined />} size="small" onClick={() => eliminarItem(r.unique_id)} />
        }
    ];

    // Cálculo financiero preciso para evitar concatenación
    const resumen = useMemo(() => {
        return items.reduce((acc, item) => {
            const cantidad = Number(item.cantidad) || 0;
            const base = Number(item.costo_base) || 0;
            const subtotalBase = base * cantidad;
            const subtotalConIva = Number(item.subtotal) || 0;
            const iva = subtotalConIva - subtotalBase;

            return {
                base: acc.base + subtotalBase,
                iva: acc.iva + iva,
                total: acc.total + subtotalConIva
            };
        }, { base: 0, iva: 0, total: 0 });
    }, [items]);

    return (
        <Modal
            title={null} open={open} onCancel={onClose} width={1200} centered destroyOnClose
            styles={{ body: { padding: '0px' } }} maskClosable={false}
            footer={
                <div style={{ padding: '16px 24px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', borderRadius: '0 0 8px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

                    {/* Desglose Contable */}
                    <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '20px' }}>
                            <Text type="secondary" style={{ fontSize: '13px' }}>Subtotal Base:</Text>
                            <Text style={{ fontSize: '13px', width: '100px' }}>${formatMoney(resumen.base.toFixed(2))}</Text>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '20px' }}>
                            <Text type="secondary" style={{ fontSize: '13px' }}>IVA Total:</Text>
                            <Text style={{ fontSize: '13px', width: '100px' }}>${formatMoney(resumen.iva.toFixed(2))}</Text>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '20px', borderTop: '1px solid #cbd5e1', paddingTop: '4px' }}>
                            <Text strong style={{ fontSize: '15px' }}>Total a Pagar:</Text>
                            <Text strong style={{ fontSize: '15px', color: '#10b981', width: '100px' }}>${formatMoney(resumen.total.toFixed(2))}</Text>
                        </div>
                    </div>

                    {/* Botones de Acción */}
                    <Space size="middle">
                        <Button size="large" onClick={onClose} style={{ borderRadius: '8px' }}>Cancelar</Button>
                        <Button type="primary" size="large" onClick={() => form.submit()} icon={<ShoppingCartOutlined />} loading={saveCompra.isLoading} style={{ background: '#22BABB', borderRadius: '8px', fontWeight: 'bold' }}>
                            {compraAEditar ? 'GUARDAR CAMBIOS' : 'PROCESAR COMPRA'}
                        </Button>
                    </Space>
                </div>
            }
        >
            <div style={{ padding: '24px' }}>
                <Space style={{ marginBottom: '20px' }}>
                    <ShopOutlined style={{ fontSize: '24px', color: '#22BABB' }} />
                    <Title level={4} style={{ margin: 0 }}>{compraAEditar ? 'Editar Factura de Compra' : 'Nueva Factura de Compra'}</Title>
                </Space>

                <Form form={form} layout="vertical" onFinish={procesarCompra}>
                    <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
                        <Row gutter={16}>
                            <Col xs={24} md={6}>
                                <Form.Item name="proveedor" label={<Text strong>Proveedor</Text>} rules={[{ required: true }]} style={{ marginBottom: '8px' }}>
                                    <Select showSearch placeholder="Buscar..." size="large" options={proveedoresDB.map(p => ({ label: p.nombre, value: p.id }))} filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())} />
                                </Form.Item>

                                {/* Bloque de información dinámica del proveedor */}
                                {proveedorInfo && (
                                    <div style={{ padding: '6px 12px', background: '#e2e8f0', borderRadius: '6px', borderLeft: '3px solid #22BABB' }}>
                                        <Text type="secondary" style={{ display: 'block', fontSize: '11px', lineHeight: '1.2' }}>
                                            <b>NIT/CC:</b> {proveedorInfo.nit || proveedorInfo.documento || 'No registrado'}
                                        </Text>
                                        <Text type="secondary" style={{ display: 'block', fontSize: '11px', lineHeight: '1.2' }}>
                                            <b>Tel:</b> {proveedorInfo.telefono || 'No registrado'}
                                        </Text>
                                    </div>
                                )}
                            </Col>
                            <Col xs={24} md={6}>
                                <Form.Item name="factura" label={<Text strong>N° Factura Física</Text>}>
                                    <Input placeholder="Ej. FAC-10029" size="large" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={6}>
                                <Form.Item name="fecha" label={<Text strong>Fecha de Emisión</Text>} rules={[{ required: true }]}>
                                    <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} size="large" allowClear={false} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={6}>
                                <Form.Item name="metodo_pago" label={<Text strong>Origen del Dinero</Text>} rules={[{ required: true }]}>
                                    <Select size="large" popupMatchSelectWidth={false}>
                                        <Option value="Efectivo Caja" disabled={!cajaActiva}><Space title={!cajaActiva ? 'No puedes seleccionar esta opción.' : ''}><WalletOutlined style={{ color: '#faad14' }} title='Hola' /> Caja Registradora</Space></Option>
                                        <Option value="Crédito"><Space><ShopOutlined style={{ color: '#ef4444' }} /> A Crédito (Por Pagar)</Space></Option>
                                        <Option value="Efectivo Externo"><Space><DollarOutlined style={{ color: '#52c41a' }} /> Efectivo Externo</Space></Option>
                                        <Option value="Transferencia"><Space><BankOutlined style={{ color: '#22BABB' }} /> Bancos / Transferencia</Space></Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            {isPremium && (
                                <Col xs={24} md={6}>
                                    <Form.Item name="bodega_destino" label={<Text strong>Bodega Destino</Text>} rules={[{ required: true, message: 'Requerido' }]} style={{ marginBottom: '8px' }}>
                                        <Select size="large" placeholder="Selecciona la bodega...">
                                            {bodegasFisicas.map(b => (
                                                <Option key={b.id} value={b.id}>{b.nombre} {b.es_default ? '(Principal)' : ''}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            )}
                        </Row>
                    </div>
                </Form>

                {/* BUSCADOR UNIFICADO OMNIBOX (Cero Lag) */}
                <div style={{ marginBottom: '16px' }}>
                    <Select
                        showSearch
                        autoFocus
                        value={null}
                        size="large"
                        placeholder="🔍 Pistolear código de barras o escribir nombre del producto..."
                        suffixIcon={<SearchOutlined />}
                        style={{ width: '100%' }}
                        onChange={(val, option) => handleSeleccionarProducto(val, option)}
                        filterOption={(input, option) => (option?.search_str ?? '').toLowerCase().includes(input.toLowerCase())}
                        options={opcionesProductos}
                    />
                </div>

                <Table
                    columns={columnas}
                    dataSource={items}
                    pagination={false}
                    size="small"
                    scroll={{ y: 300, x: 800 }}
                    style={{ border: '1px solid #e2e8f0', borderRadius: '8px' }}
                    locale={{ emptyText: 'Busca o escanea un producto para comenzar.' }}
                />
            </div>
        </Modal>
    );
};

export default ModalNuevaCompra;